/**
 * A pure, self-contained computer opponent.
 *
 * Like everything in `src/core`, this has no DOM/Solid/network dependencies: it
 * is a function from a GameState (+ the seat it controls) to a single Action —
 * the whole of that seat's turn. It reads the position through the same public
 * selectors and move generators the rules use, then scores candidate actions
 * with a heuristic position evaluation and a shallow minimax search.
 *
 * Determinism: no Math.random / Date here. Tie-breaking jitter is derived from
 * the game's own rngState so replays stay reproducible; callers who want livelier
 * play can pass their own `random`.
 */
import type { Action } from "./actions";
import { captainMove, deploy, discard, move, pass, playCard } from "./actions";
import {
  abilityDef,
  abilityDestinations,
  abilityRevives,
  abilitySources,
} from "./abilities";
import { nextRandom } from "./rng";
import { captainMovedThisTurn, reduce } from "./engine";
import { allLegalMoves, legalDeploySquaresForPiece, legalMovesFrom, mustDiscard } from "./moves";
import { pieceDef } from "./pieces";
import { findPiece, isKingCapturable, otherPlayer } from "./rules";
import { fromSquare } from "./coords";
import type { GameState, PlayerId, Square } from "./types";

export type Difficulty = "easy" | "normal" | "hard";

export interface AiOptions {
  readonly difficulty?: Difficulty;
  /** Injectable randomness for tie-breaking; defaults to the seeded game RNG. */
  readonly random?: () => number;
  /**
   * Abort hook for time-budgeted search, polled between nodes. When provided,
   * the search deepens iteratively from the difficulty's base depth toward its
   * max depth until this returns true, keeping the best move from the last
   * fully-completed depth. Without it the search runs the base depth exactly
   * (deterministic, as tests require).
   */
  readonly shouldStop?: () => boolean;
}

/** Win/loss sentinels kept well outside any reachable heuristic score. */
const WIN = 1_000_000;

interface Tuned {
  /** Base search depth in plies (1 = greedy, 2 = consider the reply). */
  readonly depth: number;
  /** Deepest iteration allowed when a time budget (shouldStop) is provided. */
  readonly maxDepth: number;
  /** How much random noise to fold into a move's score (in pawn-ish units). */
  readonly noise: number;
}

const TUNING: Readonly<Record<Difficulty, Tuned>> = {
  easy: { depth: 1, maxDepth: 1, noise: 4 },
  normal: { depth: 1, maxDepth: 2, noise: 0.75 },
  hard: { depth: 2, maxDepth: 4, noise: 0.25 },
};

/**
 * Score a position from `player`'s point of view. Higher is better for `player`.
 * Material on the board dominates; cards in hand and deck are discounted
 * potential; king safety and piece advancement are small nudges.
 */
export function evaluate(state: GameState, player: PlayerId): number {
  if (state.phase === "finished") {
    if (state.winner === player) return WIN;
    if (state.winner === otherPlayer(player)) return -WIN;
    return 0;
  }

  const foe = otherPlayer(player);
  let score = 0;

  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (!p) continue;
    const def = pieceDef(p.type);
    const sign = p.owner === player ? 1 : -1;
    score += sign * def.value;

    // Reward advancing non-royal pieces toward the enemy back rank.
    if (!def.royal && def.value > 0) {
      const { rank } = fromSquare(sq, state.config.width);
      const advance = p.owner === 0 ? rank : state.config.height - 1 - rank;
      score += sign * advance * 0.05;
    }

    // Deployed terrain (a Hill/Valley/Ridge) is a small asset — a wall worth
    // planting rather than leaving the card dead in hand. Kept tiny so it never
    // outweighs real material or an attack.
    if (def.capturable === false) score += sign * 0.3;
  }

  // Cards are latent material: hand is readily playable, deck less so.
  for (const owner of [player, foe] as const) {
    const sign = owner === player ? 1 : -1;
    const ps = state.players[owner];
    // Only piece cards carry latent material; ability cards aren't valued here
    // (the AI plays them reactively from candidateActions, but doesn't hoard them).
    for (const card of ps.hand)
      if (card.kind === "piece") score += sign * pieceDef(card.piece).value * 0.3;
    for (const card of ps.deck)
      if (card.kind === "piece") score += sign * pieceDef(card.piece).value * 0.1;
  }

  // Royalty is the whole game: the king/prince carry no material value, so
  // without an explicit term the AI has no reason to hunt them. Reward pressing
  // the enemy's royals (and penalise the enemy pressing ours) symmetrically.
  score += royaltyPressure(state, player) - royaltyPressure(state, foe);

  return score;
}

/** Chebyshev (king-move) distance between two squares. */
function chebyshev(a: Square, b: Square, width: number): number {
  const pa = fromSquare(a, width);
  const pb = fromSquare(b, width);
  return Math.max(Math.abs(pa.file - pb.file), Math.abs(pa.rank - pb.rank));
}

/**
 * How much `attacker` is threatening `defender`'s royalty. Exposing the king
 * (taking the prince) is major progress; until then the prince is the target,
 * and the closer the attacker's nearest piece sits to that target, the higher
 * the pressure. This gives the search a gradient to actually drive an attack
 * home rather than shuffle pieces forever.
 */
function royaltyPressure(state: GameState, attacker: PlayerId): number {
  const defender = otherPlayer(attacker);
  const exposed = isKingCapturable(state, defender);
  let score = exposed ? 25 : 0;

  const targetSq = findPiece(state, defender, exposed ? "king" : "prince");
  if (targetSq === null) return score;

  const width = state.config.width;
  const maxDim = Math.max(state.config.width, state.config.height) - 1;
  let nearest = Infinity;
  for (let sq = 0; sq < state.board.length; sq++) {
    const p = state.board[sq];
    if (!p || p.owner !== attacker || pieceDef(p.type).royal) continue;
    nearest = Math.min(nearest, chebyshev(sq, targetSq, width));
  }
  if (nearest !== Infinity) score += (maxDim - nearest) * 0.4;
  return score;
}

/**
 * Every action that is legal for `player` this turn. Deploy candidates are
 * deduplicated by piece type (two cards of the same type onto the same square
 * are interchangeable) to keep the branching factor sane.
 */
export function candidateActions(state: GameState, player: PlayerId): Action[] {
  if (state.phase !== "playing" || state.turn !== player) return [];

  // An over-limit hand must be discarded down first; nothing else is legal.
  if (mustDiscard(state, player)) {
    return state.players[player].hand.map((c) => discard(player, c.id));
  }

  const actions: Action[] = [];

  for (const m of allLegalMoves(state, player)) {
    actions.push(move(player, m.from, m.to));
  }

  // The Captain's free move (doesn't end the turn) — once per turn.
  if (!captainMovedThisTurn(state)) {
    for (let sq = 0; sq < state.board.length; sq++) {
      const p = state.board[sq];
      if (!p || p.owner !== player || p.type !== "captain") continue;
      for (const m of legalMovesFrom(state, sq)) actions.push(captainMove(player, sq, m.to));
    }
  }

  const seenTypes = new Set<string>();
  for (const card of state.players[player].hand) {
    if (card.kind !== "piece") continue;
    if (!pieceDef(card.piece).deployable) continue;
    if (seenTypes.has(card.piece)) continue;
    seenTypes.add(card.piece);
    for (const to of legalDeploySquaresForPiece(state, player, card.piece)) {
      actions.push(deploy(player, card.id, to));
    }
  }

  // Ability cards. Copies of the same ability are interchangeable, so expand
  // only one representative card id per ability; the targeting helpers yield
  // exactly the legal targets, so every generated play is valid.
  const seenAbilities = new Set<string>();
  for (const card of state.players[player].hand) {
    if (card.kind !== "ability" || seenAbilities.has(card.ability)) continue;
    seenAbilities.add(card.ability);
    const def = abilityDef(card.ability);
    if (def.target === "revive") {
      for (const type of abilityRevives(state, player)) {
        actions.push(playCard(player, card.id, { reviveType: type }));
      }
    } else if (def.target === "recall" || def.target === "destroy") {
      // Both pick a single board target and resolve — no destination square.
      for (const from of abilitySources(state, player, card.ability)) {
        actions.push(playCard(player, card.id, { from }));
      }
    } else {
      for (const from of abilitySources(state, player, card.ability)) {
        for (const to of abilityDestinations(state, player, card.ability, from)) {
          actions.push(playCard(player, card.id, { from, to }));
        }
      }
    }
  }

  // Drawing is automatic at the start of each turn (see engine.autoDraw), so it
  // is never a candidate the AI spends a turn on — only pass remains as the
  // do-nothing fallback when no constructive action is available.
  actions.push(pass(player));
  return actions;
}

/**
 * A cheap static priority for alpha-beta move ordering: searching the likeliest-
 * best actions first makes cutoffs happen sooner. Captures rank by victim value,
 * quiet moves and card plays follow, and pass goes last. This is ordering only —
 * it never excludes an action.
 */
function orderingKey(state: GameState, action: Action): number {
  switch (action.type) {
    case "MOVE":
    case "CAPTAIN_MOVE": {
      const victim = state.board[action.to];
      return victim ? 100 + pieceDef(victim.type).value : 50;
    }
    case "PLAY_CARD":
      return 40;
    case "DEPLOY":
      return 30;
    case "DISCARD":
      return 20;
    case "DRAW":
      return 10;
    default:
      return 0; // PASS (and anything inert) searches last
  }
}

/** Candidate actions sorted best-first for the alpha-beta search. */
function orderedCandidates(state: GameState, player: PlayerId): Action[] {
  return candidateActions(state, player)
    .map((action) => ({ action, key: orderingKey(state, action) }))
    .sort((a, b) => b.key - a.key)
    .map((e) => e.action);
}

/** Sentinel thrown to unwind an aborted (out-of-budget) search. */
const ABORTED = Symbol("search aborted");

/**
 * Alpha-beta minimax that always scores from `root`'s point of view. Using a
 * fixed perspective (rather than negamax's sign-flip) keeps it correct even
 * though a game-ending action does not flip the side to move — a winning move
 * leaves it `root`'s "turn" on a finished board, which still evaluates to +WIN.
 */
function search(
  state: GameState,
  depth: number,
  alpha: number,
  beta: number,
  root: PlayerId,
  shouldStop: (() => boolean) | undefined,
  plyFromRoot: number,
): number {
  if (shouldStop?.()) throw ABORTED;
  if (state.phase === "finished" || depth === 0) {
    const value = evaluate(state, root);
    // Discount a win/loss by its distance from the root so the search always
    // prefers the faster mate (and drags out a forced loss). Without this every
    // forced-mate line scores identically and tie-break noise can pick a slow one.
    if (value >= WIN) return value - plyFromRoot;
    if (value <= -WIN) return value + plyFromRoot;
    return value;
  }

  const mover = state.turn;
  const maximizing = mover === root;
  let best = maximizing ? -Infinity : Infinity;
  let acted = false;

  for (const action of orderedCandidates(state, mover)) {
    const result = reduce(state, action);
    if (!result.ok) continue;
    acted = true;
    // The Captain's free move doesn't end the turn, so it doesn't spend a ply of
    // search depth — the same side keeps acting (free move + real action).
    const nextDepth = action.type === "CAPTAIN_MOVE" ? depth : depth - 1;
    const value = search(result.state, nextDepth, alpha, beta, root, shouldStop, plyFromRoot + 1);
    if (maximizing) {
      if (value > best) best = value;
      if (best > alpha) alpha = best;
    } else {
      if (value < best) best = value;
      if (best < beta) beta = best;
    }
    if (alpha >= beta) break;
  }

  // No legal action at all (shouldn't happen — pass is always legal).
  return acted ? best : evaluate(state, root);
}

/**
 * Pick the action the computer should take for `player` this turn. Returns null
 * only if it is not the player's turn or the game is over.
 *
 * With `shouldStop` provided this deepens iteratively (base depth, then +1, …,
 * up to the difficulty's max), keeping the result of the last depth that
 * finished inside the budget; the previous depth's best move is searched first
 * on the next iteration, which is what makes the deeper pass affordable.
 */
export function chooseAction(
  state: GameState,
  player: PlayerId,
  options: AiOptions = {},
): Action | null {
  if (state.phase !== "playing" || state.turn !== player) return null;

  const tuning = TUNING[options.difficulty ?? "normal"];
  const actions = orderedCandidates(state, player);
  if (actions.length === 0) return null;

  // Default RNG walks the game's seeded cursor so picks are reproducible.
  let rngCursor = state.rngState;
  const random =
    options.random ??
    (() => {
      const [next, value] = nextRandom(rngCursor);
      rngCursor = next;
      return value;
    });

  // Roll each action's tie-break noise once, up front, so deeper iterations
  // refine the evaluation rather than re-rolling the dice.
  const noise = actions.map(() => (tuning.noise > 0 ? (random() - 0.5) * 2 * tuning.noise : 0));

  /** One full root pass at `depth`; null if the budget ran out mid-search. */
  function rootPass(depth: number): { action: Action; index: number } | null {
    let best: Action | null = null;
    let bestIndex = 0;
    let bestScore = -Infinity;
    try {
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i]!;
        const result = reduce(state, action);
        if (!result.ok) continue;
        // A free Captain move keeps the turn, so search it at full depth (it
        // pairs with a real action this turn rather than replacing it).
        const d = action.type === "CAPTAIN_MOVE" ? depth : depth - 1;
        let score = search(result.state, d, -Infinity, Infinity, player, options.shouldStop, 1);
        // Nudge away from idling: passing is a last resort when something
        // useful (a move, deploy, or draw) is available and scores no worse.
        if (action.type === "PASS") score -= 0.01;
        score += noise[i]!;
        if (score > bestScore) {
          bestScore = score;
          best = action;
          bestIndex = i;
        }
      }
    } catch (e) {
      if (e === ABORTED) return null;
      throw e;
    }
    return best ? { action: best, index: bestIndex } : null;
  }

  const maxDepth = options.shouldStop ? tuning.maxDepth : tuning.depth;
  let chosen: Action = actions[0]!;
  for (let depth = tuning.depth; depth <= maxDepth; depth++) {
    const pass = rootPass(depth);
    if (!pass) break; // out of budget — keep the last completed depth's pick
    chosen = pass.action;
    // Search the current best first next iteration (and keep noise aligned).
    if (pass.index > 0) {
      actions.splice(pass.index, 1);
      actions.unshift(pass.action);
      const [n] = noise.splice(pass.index, 1);
      noise.unshift(n!);
    }
    if (options.shouldStop?.()) break;
  }

  return chosen;
}
